import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACK = "__history_back__";

// Unsaved-changes guard for BrowserRouter apps: tab close (beforeunload),
// in-app link clicks (capture-phase, before React Router's handler) and
// browser Back (sentinel history entry), all behind one confirm dialog.
export function useUnsavedGuard(dirty) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPending(url.pathname + url.search);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    window.history.pushState({ __posterSentinel: true }, "");
    const onPop = () => {
      if (!dirtyRef.current) return;
      window.history.pushState({ __posterSentinel: true }, "");
      setPending(BACK);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [dirty]);

  const leave = () => {
    const to = pending;
    setPending(null);
    dirtyRef.current = false;
    if (to === BACK) window.history.go(-2);
    else if (to) navigate(to);
  };

  const guardedNavigate = (to) => (dirtyRef.current ? setPending(to) : navigate(to));

  const dialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
      <AlertDialogContent data-testid="poster-unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>Your poster has changes that haven&apos;t been saved. If you leave now they will be lost.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="poster-unsaved-stay">Stay on this page</AlertDialogCancel>
          <AlertDialogAction onClick={leave} data-testid="poster-unsaved-leave">Leave without saving</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { dialog, guardedNavigate };
}
